// The Pulse icon set — 44 glyphs replacing the emoji that used to sit in nav
// rows, tab strips and page headers. See docs/ICON_SYSTEM.md for the why and
// docs/mockups/icon-system.html for the mockup this came from.
//
// Drawing rules (match components/HomeButton.jsx and the old PieIcon.jsx, so
// nothing already drawn had to be redrawn):
//   * 24x24 canvas, artwork inside the middle 20 (2px keepout)
//   * 2px stroke, round caps + joins, never scaled
//   * currentColor only — an icon never sets its own colour, so it tints with
//     the row it sits in (muted -> brand when active). This is the whole point:
//     an emoji could not do it.
//   * four drawn elements at the very most; half the set uses two
//
// Artwork is inlined per instance rather than referenced from an SVG <symbol>
// sprite on purpose: <use> clones into a shadow tree that document CSS cannot
// reach, and a sprite has to be mounted above every consumer. Inline paths work
// in any subtree — modals, portals, print views — with no mount order to get
// wrong.
//
// KEEP EACH GLYPH'S ELEMENT ORDER STABLE. The sidebar's hover animations reach
// into the artwork positionally — `svg[data-icon="campaigns"] path:nth-child(2)`
// is the megaphone's inner sound wave, `…[data-icon="goals"] circle:nth-child(1)`
// is the target's outer ring (see "Icon micro-interactions" in index.css).
// Reordering the shapes inside one of those glyphs won't error; the animation
// will just quietly move to the wrong part of the drawing.

export const ICONS = {
  // Dashboards / Home
  dashboards: (
    <>
      <rect x="3" y="3" width="7.6" height="7.6" rx="2" />
      <rect x="13.4" y="3" width="7.6" height="7.6" rx="2" />
      <rect x="3" y="13.4" width="7.6" height="7.6" rx="2" />
      <rect x="13.4" y="13.4" width="7.6" height="7.6" rx="2" />
    </>
  ),
  // App Analytics
  app: (
    <>
      <rect x="6" y="2.5" width="12" height="19" rx="2.6" />
      <path d="M9.5 17v-3.6M12 17v-6.6M14.5 17v-4.8" />
      <path d="M10.6 5.4h2.8" />
    </>
  ),
  // Goals
  goals: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <circle cx="12" cy="12" r="4.6" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  // Alerts
  alerts: (
    <>
      <path d="M18 15.4V10a6 6 0 0 0-12 0v5.4L4.4 18h15.2L18 15.4Z" />
      <path d="M9.7 20.8a2.5 2.5 0 0 0 4.6 0" />
    </>
  ),
  // Event Ops
  eventops: (
    <>
      <path d="M4 14.5v-2.4a8 8 0 0 1 16 0v2.4" />
      <rect x="2.2" y="13.6" width="4" height="6.6" rx="2" />
      <rect x="17.8" y="13.6" width="4" height="6.6" rx="2" />
    </>
  ),
  // Event Map
  map: (
    <>
      <path d="M3 6.6 9 4.2l6 2.4 6-2.4v13.2l-6 2.4-6-2.4-6 2.4V6.6Z" />
      <path d="M9 4.2v13.2M15 6.6v13.2" />
    </>
  ),
  // Digests
  digests: (
    <>
      <rect x="3.4" y="5" width="17.2" height="15.6" rx="2.6" />
      <path d="M3.4 10h17.2M8.4 3.2v3.6M15.6 3.2v3.6" />
      <path d="M7.6 14h3.2M13.2 14h3.2M7.6 17.2h3.2" />
    </>
  ),
  // Settlements
  settlements: (
    <>
      <path d="M6 3h12v18l-3-1.8-3 1.8-3-1.8L6 21V3Z" />
      <path d="M9.4 8h5.2M9.4 12h5.2" />
    </>
  ),
  // Budget — a ledger sheet with a rising forecast line
  budget: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" />
      <path d="M7 14.6 10.2 11l2.6 2.2L17 8.6" />
      <path d="M7 18h10" />
    </>
  ),
  // Inbox
  inbox: (
    <>
      <path d="M4 13.8v3.9a2.3 2.3 0 0 0 2.3 2.3h11.4a2.3 2.3 0 0 0 2.3-2.3v-3.9Z" />
      <path d="M4 13.8h5l1 2.4h4l1-2.4h5" />
      <path d="M12 3v7.6m0 0 2.8-2.8M12 10.6 9.2 7.8" />
    </>
  ),
  // Your journey
  journey: (
    <>
      <path d="M5.6 17.2c0-3.4 2.6-4.4 6.4-4.4s6.4-1 6.4-4.6" />
      <circle cx="5" cy="19.4" r="2.2" />
      <circle cx="19" cy="5.6" r="2.2" />
      <circle cx="12" cy="12.8" r="1.3" />
    </>
  ),
  // Tickets / Product
  ticket: (
    <>
      <path d="M3 9.6V7.4A1.6 1.6 0 0 1 4.6 5.8h14.8A1.6 1.6 0 0 1 21 7.4v2.2a2.4 2.4 0 0 0 0 4.8v2.2a1.6 1.6 0 0 1-1.6 1.6H4.6A1.6 1.6 0 0 1 3 16.6v-2.2a2.4 2.4 0 0 0 0-4.8Z" />
      <path d="M8.4 9.2v1.8M8.4 13v1.8" />
    </>
  ),
  // Report an issue
  chat: <><path d="M21 11.6a8.4 8.4 0 0 1-8.5 8.4 9.2 9.2 0 0 1-3.7-.8L4 21l1.4-4.2A8.4 8.4 0 0 1 12.5 3.2 8.4 8.4 0 0 1 21 11.6Z" /></>,
  // Community
  community: (
    <>
      <path d="M16.4 11.4a6.5 6.5 0 0 1-6.6 6.5 7 7 0 0 1-2.7-.5L4 18.6l1-2.9a6.4 6.4 0 0 1 4.8-10.8 6.5 6.5 0 0 1 6.6 6.5Z" />
      <path d="M17.6 6.6A5.6 5.6 0 0 1 20 14.4l.8 2.3-2.4-.8" />
    </>
  ),
  // Campaigns
  campaigns: (
    <>
      <path d="M4 14.6V9.4A1.6 1.6 0 0 1 5.6 7.8h2.2L14 3.6v16.8l-6.2-4.2H5.6A1.6 1.6 0 0 1 4 14.6Z" />
      <path d="M17.4 9.2a4.4 4.4 0 0 1 0 5.6" />
      <path d="M19.8 6.6a8 8 0 0 1 0 10.8" />
    </>
  ),
  // Segments (was PieIcon)
  segments: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 12V3" />
      <path d="M12 12h9" />
    </>
  ),
  // Social
  social: (
    <>
      <circle cx="18" cy="5.6" r="2.6" />
      <circle cx="6" cy="12" r="2.6" />
      <circle cx="18" cy="18.4" r="2.6" />
      <path d="M8.3 10.7 15.7 6.9M8.3 13.3l7.4 3.8" />
    </>
  ),
  // The Owl / ask AI
  owl: (
    <>
      <path d="M4.6 4.2 8 7.2a7.3 7.3 0 0 1 8 0l3.4-3-.6 4.3c.4.9.6 1.9.6 3v1.9a7.4 7.4 0 0 1-14.8 0v-1.9c0-1.1.2-2.1.6-3l-.6-4.3Z" />
      <circle cx="9.3" cy="11.6" r="1.9" />
      <circle cx="14.7" cy="11.6" r="1.9" />
      <path d="M12 14.6 10.8 16.4h2.4L12 14.6Z" />
    </>
  ),
  // Spark — the native chat without the data-analysis layer (a lightning bolt)
  spark: (
    <path d="M13 2.4 4.4 13.2h6.2l-1.6 8.4 8.6-10.8h-6.2L13 2.4Z" />
  ),
  // AI insight
  insights: (
    <>
      <path d="M11 3.4 12.6 8 17.2 9.6 12.6 11.2 11 15.8 9.4 11.2 4.8 9.6 9.4 8 11 3.4Z" />
      <path d="M17.6 15.4l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" />
    </>
  ),
  // Flags
  flags: (
    <>
      <path d="M5.6 21V3.6" />
      <path d="M5.6 4.4h11.6l-2.2 3.8 2.2 3.8H5.6" />
    </>
  ),
  // Moderation
  moderation: (
    <>
      <path d="M12 20.8s7.4-3.4 7.4-8.8V5.6L12 3.2 4.6 5.6V12c0 5.4 7.4 8.8 7.4 8.8Z" />
      <path d="M9 11.7l2.3 2.3 4-4.2" />
    </>
  ),
  // Settings
  settings: (
    <>
      <path d="M3.6 7.4h8.6M17.2 7.4h3.2M3.6 16.6h3.2M12.2 16.6h8.2" />
      <circle cx="14.6" cy="7.4" r="2.4" />
      <circle cx="9.6" cy="16.6" r="2.4" />
    </>
  ),
  // Clients & people
  clients: (
    <>
      <circle cx="9.4" cy="8" r="3.4" />
      <path d="M3.6 20.2a5.8 5.8 0 0 1 11.6 0" />
      <circle cx="17.6" cy="9.6" r="2.3" />
      <path d="M16.6 15.4a4.6 4.6 0 0 1 3.8 4.8" />
    </>
  ),
  // Code health
  health: <><path d="M2.8 12h3.6l2-4.8 3 10 2.6-6 1.6 3.4h5.6" /></>,
  // Release notes
  notes: (
    <>
      <path d="M13.6 3.4H7A2.2 2.2 0 0 0 4.8 5.6v12.8A2.2 2.2 0 0 0 7 20.6h10a2.2 2.2 0 0 0 2.2-2.2v-7" />
      <path d="M8.4 9.2h4.4M8.4 13.2h3" />
      <path d="M17.4 3.6a1.9 1.9 0 0 1 2.7 2.7l-4.3 4.3-2.9.6.6-2.9 3.9-4.7Z" />
    </>
  ),
  // Help knowledge
  help: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <circle cx="12" cy="12" r="3.4" />
      <path d="M6 6l3.6 3.6M18 6l-3.6 3.6M6 18l3.6-3.6M18 18l-3.6-3.6" />
    </>
  ),
  // Surveys
  surveys: (
    <>
      <rect x="4.6" y="4.4" width="14.8" height="16.2" rx="2.4" />
      <path d="M9 4.4V3.2h6v1.2" />
      <path d="M8 10.2l1.5 1.5 2.6-2.7M8 15.8l1.5 1.5 2.6-2.7" />
      <path d="M14.4 10.2h2.4M14.4 15.8h2.4" />
    </>
  ),
  // Deep links
  links: (
    <>
      <path d="M10.2 13.6a4.2 4.2 0 0 0 6 0l2.6-2.6a4.2 4.2 0 0 0-6-6L11.6 6.2" />
      <path d="M13.8 10.4a4.2 4.2 0 0 0-6 0L5.2 13a4.2 4.2 0 0 0 6 6l1.2-1.2" />
    </>
  ),
  // Skills
  skills: (
    <>
      <rect x="6.6" y="6.6" width="10.8" height="10.8" rx="2.6" />
      <path d="M9.8 3.2v3.4M14.2 3.2v3.4M9.8 17.4v3.4M14.2 17.4v3.4M3.2 9.8h3.4M3.2 14.2h3.4M17.4 9.8h3.4M17.4 14.2h3.4" />
    </>
  ),
  // Map Studio
  layers: (
    <>
      <path d="M12 3 3.2 7.4 12 11.8l8.8-4.4L12 3Z" />
      <path d="M3.2 12.4 12 16.8l8.8-4.4M3.2 16.6 12 21l8.8-4.4" />
    </>
  ),
  // Feature matrix
  matrix: (
    <>
      <rect x="3.4" y="3.4" width="17.2" height="17.2" rx="2.6" />
      <path d="M3.4 9.4h17.2M9.4 3.4v17.2" />
      <path d="M12.6 14.4l1.8 1.8 3.2-3.6" />
    </>
  ),
  // AM scorecard
  scorecard: (
    <>
      <path d="M8 3.6h8v4.8a4 4 0 0 1-8 0V3.6Z" />
      <path d="M8 5.2H5.4v1.4a3 3 0 0 0 2.8 3M16 5.2h2.6v1.4a3 3 0 0 1-2.8 3" />
      <path d="M12 12.6v3.4M8.4 20.4h7.2" />
      <path d="M9.4 20.4a2.6 2.6 0 0 1 5.2 0" />
    </>
  ),
  // Event Media
  media: (
    <>
      <rect x="3" y="4.4" width="18" height="15.2" rx="2.6" />
      <circle cx="8.6" cy="9.4" r="1.8" />
      <path d="M3.6 17.6l4.8-4.6 3.4 3.2 4-4.2 4.6 4.8" />
    </>
  ),
  // Lineup Studio (a microphone)
  lineup: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5.5 11a6.5 6.5 0 0013 0M12 17.5V21M8.5 21h7" />
    </>
  ),
  // Reports
  reports: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2.4" />
      <path d="M14.6 3v5.6l-2.3-1.8L10 8.6V3" />
      <path d="M8.4 13.4h7.2M8.4 16.8h4.6" />
    </>
  ),
  // Email / journey emails
  email: (
    <>
      <rect x="3" y="5.4" width="18" height="13.2" rx="2.6" />
      <path d="M3.6 7.6 12 13.2l8.4-5.6" />
    </>
  ),
  // Pinned a tile
  pin: (
    <>
      <path d="M9.2 3.2h5.6l-.8 5.6 3.4 3.2H6.6L10 8.8l-.8-5.6Z" />
      <path d="M12 12.2V21" />
    </>
  ),
  // Followed a tile
  eye: (
    <>
      <path d="M2.4 12S6 6.2 12 6.2 21.6 12 21.6 12 18 17.8 12 17.8 2.4 12 2.4 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  // Live
  live: (
    <>
      <circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none" />
      <path d="M7.9 7.9a5.8 5.8 0 0 0 0 8.2M16.1 16.1a5.8 5.8 0 0 0 0-8.2" />
      <path d="M5.1 5.1a9.8 9.8 0 0 0 0 13.8M18.9 18.9a9.8 9.8 0 0 0 0-13.8" />
    </>
  ),
  // Location
  location: (
    <>
      <path d="M12 21s6.6-6.2 6.6-11.2a6.6 6.6 0 1 0-13.2 0C5.4 14.8 12 21 12 21Z" />
      <circle cx="12" cy="9.8" r="2.4" />
    </>
  ),
  // Attention / warning
  warn: (
    <>
      <path d="M12 3.8 21 19.6H3L12 3.8Z" />
      <path d="M12 9.8v4.4M12 17.2v.2" />
    </>
  ),
  // Done / sent
  check: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M8.2 12.3l2.6 2.6 5-5.4" />
    </>
  ),
  // Pending
  pending: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M12 7.4V12l3.2 2.1" />
    </>
  ),
  // Secret / locked
  lock: (
    <>
      <rect x="4.4" y="10.4" width="15.2" height="10.2" rx="2.6" />
      <path d="M7.8 10.4V7.8a4.2 4.2 0 0 1 8.4 0v2.6" />
    </>
  ),
  // Beta / test
  beta: (
    <>
      <path d="M9 3.2v6.4l-4.2 7.6A2 2 0 0 0 6.6 20.4h10.8a2 2 0 0 0 1.8-3.2L15 9.6V3.2" />
      <path d="M7.8 3.2h8.4M6.6 14.6h10.8" />
    </>
  ),
  // Trend
  trend: (
    <>
      <path d="M3.4 20.2V3.8M3.4 20.2h17.2" />
      <path d="M6.8 16.2 10.4 11.4l3 2.4 6-6.4" />
    </>
  ),

  // ── Utility glyphs ────────────────────────────────────────────────────────
  // Not in the mockup's 42 — these turned up while converting real screens and
  // follow the same rules. Monochrome dingbats (✕ ✓ ✎ ☰ →) are deliberately
  // NOT here: they're text glyphs that already inherit colour and scale with
  // font-size, so they were never part of the problem.

  // Suite category (was a folder-tab emoji)
  folder: (
    <>
      <path d="M3.2 7.4a2 2 0 0 1 2-2h3.4l2 2.4h8.2a2 2 0 0 1 2 2v8.4a2 2 0 0 1-2 2H5.2a2 2 0 0 1-2-2V7.4Z" />
    </>
  ),
  // Light mode
  sun: (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.6v2.4M12 19v2.4M4.4 12H2M22 12h-2.4M6.6 6.6 4.9 4.9M19.1 19.1l-1.7-1.7M17.4 6.6l1.7-1.7M4.9 19.1l1.7-1.7" />
    </>
  ),
  // Dark mode
  moon: (
    <>
      <path d="M20.4 14.6A8.8 8.8 0 0 1 9.4 3.6a8.8 8.8 0 1 0 11 11Z" />
    </>
  ),
  // Sent back / returned to draft
  undo: (
    <>
      <path d="M4 9.4h10.6a4.8 4.8 0 0 1 0 9.6H8.4" />
      <path d="M7.6 5.4 3.6 9.4l4 4" />
    </>
  ),

  // Event media / app posters / any picture surface
  image: (
    <>
      <rect x="3.2" y="4.6" width="17.6" height="14.8" rx="2.6" />
      <circle cx="8.8" cy="9.8" r="1.8" />
      <path d="M3.6 16.4 9 11.6l4.4 4 2.8-2.4 4.2 3.6" />
    </>
  ),
  // Whole app / global / public
  web: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M3.4 12h17.2" />
      <path d="M12 3.4c2.6 2.4 2.6 14.8 0 17.2M12 3.4c-2.6 2.4-2.6 14.8 0 17.2" />
    </>
  ),

  // ── Engage / Product surfaces ─────────────────────────────────────────────
  // Feed posts
  feed: (
    <>
      <rect x="3.2" y="4.6" width="17.6" height="14.8" rx="2.4" />
      <path d="M6.6 8.6h6M6.6 12h6M6.6 15.4h4" />
      <path d="M16 8.6h1.6M16 12h1.6M16 15.4h1.6" />
    </>
  ),
  // Report a bug
  bug: (
    <>
      <rect x="7.6" y="7.4" width="8.8" height="12" rx="4.4" />
      <path d="M9.4 7.4a2.6 2.6 0 0 1 5.2 0" />
      <path d="M7.6 11.4H4.2M16.4 11.4h3.4M7.6 15.6H4.6M16.4 15.6h3" />
    </>
  ),
  // An idea
  idea: (
    <>
      <path d="M9 17.4a6.4 6.4 0 1 1 6 0v1.8a1.6 1.6 0 0 1-1.6 1.6h-2.8A1.6 1.6 0 0 1 9 19.2v-1.8Z" />
      <path d="M9.4 18.6h5.2" />
    </>
  ),
  // Screen recording
  record: (
    <>
      <rect x="2.6" y="6.4" width="12.4" height="11.2" rx="2.4" />
      <path d="M15 11.2l5.4-2.8v7.2L15 12.8Z" />
    </>
  ),
  // Abandoned carts
  cart: (
    <>
      <path d="M2.8 4.2h2.6l2.4 10.4h9.2l2.2-7.6H6.6" />
      <circle cx="9.4" cy="19" r="1.5" />
      <circle cx="16.6" cy="19" r="1.5" />
    </>
  ),
  // Returning / repeat
  repeat: (
    <>
      <path d="M4 9.6a6 6 0 0 1 6-6h8" />
      <path d="M15.4 1.2 18.8 3.6 15.4 6" />
      <path d="M20 14.4a6 6 0 0 1-6 6H6" />
      <path d="M8.6 17.6 5.2 20.4 8.6 22.8" />
    </>
  ),
  // Most loyal
  heart: (
    <>
      <path d="M12 20.4S3.6 15.6 3.6 9.8a4.6 4.6 0 0 1 8.4-2.6 4.6 4.6 0 0 1 8.4 2.6c0 5.8-8.4 10.6-8.4 10.6Z" />
    </>
  ),
  // Top spenders
  gem: (
    <>
      <path d="M7.4 3.6h9.2l4 5.4L12 20.6 3.4 9Z" />
      <path d="M3.4 9h17.2M9.2 3.6 7.4 9l4.6 11.6L16.6 9l-1.8-5.4" />
    </>
  ),
  // Branding / theme
  palette: (
    <>
      <path d="M12 3.4a8.6 8.6 0 0 0 0 17.2c1.3 0 1.9-.9 1.9-1.8 0-1.2-1-1.6-1-2.7 0-.8.7-1.5 1.6-1.5h1.7a4.4 4.4 0 0 0 4.4-4.4c0-3.7-3.9-6.8-8.6-6.8Z" />
      <circle cx="8" cy="10.4" r="1.2" />
      <circle cx="12.4" cy="7.8" r="1.2" />
      <circle cx="16.4" cy="10.2" r="1.2" />
    </>
  ),
  // A list of people / rows
  list: (
    <>
      <path d="M8.6 6.4h12M8.6 12h12M8.6 17.6h12" />
      <circle cx="4.2" cy="6.4" r="1.3" />
      <circle cx="4.2" cy="12" r="1.3" />
      <circle cx="4.2" cy="17.6" r="1.3" />
    </>
  ),

  // ── Event Ops: station kinds & device handling ────────────────────────────
  // Bar
  bar: (
    <>
      <path d="M5 4.2h14l-6 7.2v6.4h3.4v2.2H7.6v-2.2H11v-6.4L5 4.2Z" />
      <path d="M7.2 7h9.6" />
    </>
  ),
  // Food & bev / vendor
  food: (
    <>
      <path d="M6.4 3.2v7.2a2.4 2.4 0 0 0 4.8 0V3.2" />
      <path d="M8.8 10.4V20.8" />
      <path d="M17.4 3.2c-1.6 1.2-2.4 3-2.4 5.2 0 1.7.8 2.8 2.4 3.2V20.8" />
    </>
  ),
  // Access control / gate
  gate: (
    <>
      <path d="M4.4 20.6V8.6a7.6 7.6 0 0 1 15.2 0v12" />
      <path d="M9.2 20.6v-12a2.8 2.8 0 0 1 5.6 0v12" />
      <path d="M3 20.6h18" />
    </>
  ),
  // Booth / kiosk
  booth: (
    <>
      <path d="M3.4 8.6 5 4.4h14l1.6 4.2a3 3 0 0 1-5.8 1 3 3 0 0 1-5.6 0 3 3 0 0 1-5.8-1Z" />
      <path d="M5 11.4v9.2h14v-9.2" />
      <path d="M9.4 20.6v-5.2h5.2v5.2" />
    </>
  ),
  // A handheld device / terminal
  device: (
    <>
      <rect x="5.4" y="2.6" width="13.2" height="18.8" rx="2.4" />
      <path d="M8.6 6.4h6.8" />
      <path d="M8.6 11h2.2M13 11h2.4M8.6 15h2.2M13 15h2.4" />
    </>
  ),
  // The Hive — where devices live between deployments
  hive: (
    <>
      <path d="M8.2 3.4h7.6l3.8 6.6-3.8 6.6H8.2L4.4 10Z" />
      <path d="M10.4 8h3.2l1.6 2.8-1.6 2.8h-3.2L8.8 10.8Z" />
      <path d="M12 16.6v4.2" />
    </>
  ),
  // Scan a QR code
  scan: (
    <>
      <path d="M3.4 8.4V5.8a2.4 2.4 0 0 1 2.4-2.4h2.6M15.6 3.4h2.6a2.4 2.4 0 0 1 2.4 2.4v2.6M20.6 15.6v2.6a2.4 2.4 0 0 1-2.4 2.4h-2.6M8.4 20.6H5.8a2.4 2.4 0 0 1-2.4-2.4v-2.6" />
      <path d="M3.4 12h17.2" />
    </>
  ),
  // Move / shuffle between places
  shuffle: (
    <>
      <path d="M3.4 7.4h4.2l8.8 9.2h4.2" />
      <path d="M3.4 16.6h4.2l3-3.2M13.4 10 16.4 7.4h4.2" />
      <path d="M18.2 4.6 20.8 7.4l-2.6 2.8M18.2 13.8l2.6 2.8-2.6 2.8" />
    </>
  ),
  // A label / category tag
  tag: (
    <>
      <path d="M11.4 3.4H4.6a1.2 1.2 0 0 0-1.2 1.2v6.8a2 2 0 0 0 .6 1.4l7 7a2 2 0 0 0 2.8 0l6.2-6.2a2 2 0 0 0 0-2.8l-7-7a2 2 0 0 0-1.6-.4Z" />
      <circle cx="7.8" cy="7.8" r="1.4" />
    </>
  ),
  // Delete
  trash: (
    <>
      <path d="M3.8 6.4h16.4" />
      <path d="M9 6.4V4.2h6v2.2" />
      <path d="M5.8 6.4l1 13.2a1.4 1.4 0 0 0 1.4 1.2h7.6a1.4 1.4 0 0 0 1.4-1.2l1-13.2" />
    </>
  ),
  // Edit / rename
  edit: (
    <>
      <path d="M4 20h4.2l11-11a2.6 2.6 0 0 0-3.6-3.6L4.6 16.4 4 20Z" />
      <path d="M14.6 6.8 17.2 9.4" />
    </>
  ),
  // Default / favourite
  star: (
    <>
      <path d="M12 3.4l2.6 5.4 5.8.8-4.2 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8L3.6 9.6l5.8-.8L12 3.4Z" />
    </>
  ),
  // Search / filter
  search: (
    <>
      <circle cx="10.8" cy="10.8" r="6.6" />
      <path d="M15.6 15.6 20.6 20.6" />
    </>
  ),
  // Home / in stock
  home: (
    <>
      <path d="M3.2 10.4 12 3.4l8.8 7" />
      <path d="M5.4 9v10.6a1.4 1.4 0 0 0 1.4 1.4h10.4a1.4 1.4 0 0 0 1.4-1.4V9" />
    </>
  ),
  // Add
  plus: <><path d="M12 4.6v14.8M4.6 12h14.8" /></>,
  // Flow / throughput
  flow: (
    <>
      <path d="M2.8 8.2c2.4-2.6 4.8-2.6 7.2 0s4.8 2.6 7.2 0 4.8-2.6 4 0" />
      <path d="M2.8 15.4c2.4-2.6 4.8-2.6 7.2 0s4.8 2.6 7.2 0 4.8-2.6 4 0" />
    </>
  ),

  // ── Admin & misc ──────────────────────────────────────────────────────────
  // A client / organisation
  building: (
    <>
      <path d="M4.4 20.8V4.6a1.2 1.2 0 0 1 1.2-1.2h8.4a1.2 1.2 0 0 1 1.2 1.2v16.2" />
      <path d="M15.2 10h3.4a1.2 1.2 0 0 1 1.2 1.2v9.6" />
      <path d="M3 20.8h18M7.8 7h3.6M7.8 11h3.6M7.8 15h3.6" />
    </>
  ),
  // Logins / credentials
  key: (
    <>
      <circle cx="8" cy="8" r="4.4" />
      <path d="M11.2 11.2 20.6 20.6" />
      <path d="M17.4 17.4 19.6 15.2M14.6 14.6 16.8 12.4" />
    </>
  ),
  // Revenue / currency
  money: (
    <>
      <circle cx="12" cy="12" r="8.6" />
      <path d="M14.6 9c-.5-.9-1.5-1.4-2.6-1.4-1.6 0-2.8.9-2.8 2.2 0 3 5.6 1.6 5.6 4.6 0 1.3-1.2 2.2-2.8 2.2-1.1 0-2.1-.5-2.6-1.4" />
      <path d="M12 6v12" />
    </>
  ),
  // Web / desktop surface
  desktop: (
    <>
      <rect x="2.8" y="4.2" width="18.4" height="12.4" rx="2.2" />
      <path d="M8.4 20.6h7.2M12 16.6v4" />
    </>
  ),
  // An attachment
  attach: (
    <>
      <path d="M20 11.4 12.2 19.2a4.8 4.8 0 0 1-6.8-6.8l8.2-8.2a3.2 3.2 0 0 1 4.6 4.6l-8.2 8.2a1.6 1.6 0 0 1-2.2-2.2l7.4-7.4" />
    </>
  ),
  // Unlocked
  unlock: (
    <>
      <rect x="4.4" y="10.4" width="15.2" height="10.2" rx="2.6" />
      <path d="M7.8 10.4V7.8a4.2 4.2 0 0 1 8.1-1.5" />
    </>
  ),
  // Hidden from view
  eyeoff: (
    <>
      <path d="M9.6 6.6A9.4 9.4 0 0 1 12 6.2c6 0 9.6 5.8 9.6 5.8a17 17 0 0 1-3 3.7M6.4 8.1A16.6 16.6 0 0 0 2.4 12s3.6 5.8 9.6 5.8a9.5 9.5 0 0 0 3.4-.6" />
      <path d="M10 10a2.8 2.8 0 0 0 4 4" />
      <path d="M3.4 3.4 20.6 20.6" />
    </>
  ),
  // Negative feedback
  thumbdown: (
    <>
      <path d="M6.6 3.6h9.8l2.6 8.2h-3.4l1 5.4a2.2 2.2 0 0 1-4.2 1.2L9 11.8H6.6Z" />
      <path d="M3.4 3.6h3.2v8.2H3.4Z" />
    </>
  ),

  // ── Admin sidebar ─────────────────────────────────────────────────────────
  // Setup wizard
  wizard: (
    <>
      <path d="M4.6 19.4 13.8 10.2" />
      <path d="M17.6 3.6l1.2 2.8 2.8 1.2-2.8 1.2-1.2 2.8-1.2-2.8-2.8-1.2 2.8-1.2 1.2-2.8Z" />
      <path d="M7.6 6.2l.7 1.6 1.6.7-1.6.7-.7 1.6-.7-1.6-1.6-.7 1.6-.7.7-1.6Z" />
    </>
  ),
  // A single user (Clients uses `clients`, which is two people)
  user: (
    <>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5 20.4a7 7 0 0 1 14 0" />
    </>
  ),
  // Onboarding
  rocket: (
    <>
      <path d="M12 3.2c2.6 2.2 4 5.4 4 8.8 0 2-.6 3.8-1.6 5.4H9.6A10 10 0 0 1 8 12c0-3.4 1.4-6.6 4-8.8Z" />
      <circle cx="12" cy="10.4" r="1.8" />
      <path d="M9.6 17.4 8 20.8l2.6-1.2M14.4 17.4 16 20.8l-2.6-1.2" />
    </>
  ),
  // Billing
  card: (
    <>
      <rect x="2.6" y="5.4" width="18.8" height="13.2" rx="2.6" />
      <path d="M2.6 10h18.8" />
      <path d="M6.4 14.4h3.2" />
    </>
  ),
  // Integrations
  plug: (
    <>
      <path d="M9 3.4v5M15 3.4v5" />
      <path d="M6.4 8.4h11.2v2.4a5.6 5.6 0 0 1-11.2 0V8.4Z" />
      <path d="M12 16.4v4.2" />
    </>
  ),
  // Data health — a feed radiating in
  signal: (
    <>
      <circle cx="12" cy="12" r="1.6" />
      <path d="M8.4 8.8a5 5 0 0 1 7.2 0" />
      <path d="M5.6 6a9 9 0 0 1 12.8 0" />
      <path d="M12 13.6v7" />
    </>
  ),
  // Training
  training: (
    <>
      <path d="M2.8 8.6 12 4.4l9.2 4.2L12 12.8 2.8 8.6Z" />
      <path d="M6.6 10.6v4.6c0 1.6 2.4 2.8 5.4 2.8s5.4-1.2 5.4-2.8v-4.6" />
      <path d="M20.2 9.6v5" />
    </>
  ),
  // Backup
  database: (
    <>
      <ellipse cx="12" cy="6" rx="7.6" ry="3" />
      <path d="M4.4 6v6c0 1.7 3.4 3 7.6 3s7.6-1.3 7.6-3V6" />
      <path d="M4.4 12v6c0 1.7 3.4 3 7.6 3s7.6-1.3 7.6-3v-6" />
    </>
  ),
};

export const ICON_NAMES = Object.keys(ICONS);
